import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Trash2, Edit, Plus, RefreshCw, Package } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import RichTextEditor from '@/components/RichTextEditor';

const rewardSchema = z.object({
  name: z.string().min(1, '商品名稱為必填'),
  name_en: z.string().optional(),
  description: z.string().optional(),
  description_en: z.string().optional(),
  points_required: z.number().min(1, '所需積分必須大於0'),
  stock_quantity: z.number().min(0, '庫存數量不能為負數').optional(),
  image_url: z.string().url('請輸入有效的圖片網址').optional().or(z.literal('')),
  category: z.string().min(1, '分類為必填'),
  is_active: z.boolean(),
});

type RewardFormData = z.infer<typeof rewardSchema>;

interface Reward {
  id: string;
  name: string;
  name_en?: string;
  description?: string;
  description_en?: string;
  points_required: number;
  stock_quantity?: number;
  image_url?: string;
  category: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const RewardManagement = () => {
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingReward, setEditingReward] = useState<Reward | null>(null);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const form = useForm<RewardFormData>({
    resolver: zodResolver(rewardSchema),
    defaultValues: {
      name: '',
      name_en: '',
      description: '',
      description_en: '',
      points_required: 100,
      stock_quantity: 0,
      image_url: '',
      category: 'general',
      is_active: true,
    },
  });

  useEffect(() => {
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('rewards')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRewards(data || []);
    } catch (error: any) {
      console.error('Error fetching rewards:', error);
      toast({
        variant: 'destructive',
        title: '載入失敗',
        description: error?.message || '無法載入獎品列表',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (data: RewardFormData) => {
    try {
      if (!data.name || !data.points_required || !data.category) {
        throw new Error('商品名稱、所需積分和分類為必填項目');
      }

      const rewardData = {
        name: data.name,
        name_en: data.name_en || null,
        description: data.description || null,
        description_en: data.description_en || null,
        points_required: data.points_required,
        stock_quantity: data.stock_quantity || null,
        image_url: data.image_url || null,
        category: data.category,
        is_active: data.is_active,
      };

      if (editingReward) {
        const { error } = await supabase
          .from('rewards')
          .update({
            ...rewardData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingReward.id);

        if (error) throw error;

        toast({
          title: '更新成功',
          description: '獎品資訊已更新',
        });
      } else {
        const { error } = await supabase
          .from('rewards')
          .insert(rewardData);

        if (error) throw error;

        toast({
          title: '新增成功',
          description: '新獎品已新增',
        });
      }

      setIsDialogOpen(false);
      setEditingReward(null);
      form.reset();
      fetchRewards();
    } catch (error: any) {
      console.error('Error saving reward:', error);
      toast({
        variant: 'destructive',
        title: editingReward ? '更新失敗' : '新增失敗',
        description: error?.message || '操作失敗，請重試',
      });
    }
  };

  const handleEdit = (reward: Reward) => {
    setEditingReward(reward);
    form.reset({
      name: reward.name,
      name_en: reward.name_en || '',
      description: reward.description || '',
      description_en: reward.description_en || '',
      points_required: reward.points_required,
      stock_quantity: reward.stock_quantity || 0,
      image_url: reward.image_url || '',
      category: reward.category,
      is_active: reward.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (rewardId: string) => {
    if (window.confirm('確定要刪除此獎品嗎？此操作無法復原。')) {
      try {
        const { error } = await supabase
          .from('rewards')
          .delete()
          .eq('id', rewardId);

        if (error) throw error;

        toast({
          title: '刪除成功',
          description: '獎品已刪除',
        });
        fetchRewards();
      } catch (error: any) {
        console.error('Error deleting reward:', error);
        toast({
          variant: 'destructive',
          title: '刪除失敗',
          description: error?.message || '無法刪除獎品',
        });
      }
    }
  };

  const handleCreateNew = () => {
    setEditingReward(null);
    form.reset();
    setIsDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-2 text-gray-600">載入獎品列表中...</span>
      </div>
    );
  }

  // 手機版卡片布局
  if (isMobile) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col space-y-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">獎品管理</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">管理積分兌換商品</p>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">共 {rewards.length} 項獎品</span>
            <div className="flex space-x-2">
              <Button onClick={fetchRewards} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={handleCreateNew} size="sm">
                    <Plus className="h-4 w-4 mr-1" />
                    新增
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingReward ? '編輯獎品' : '新增獎品'}</DialogTitle>
                  </DialogHeader>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>商品名稱（中文）*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="name_en"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>商品名稱（英文）</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="English product name" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>商品描述（中文）</FormLabel>
                            <FormControl>
                              <RichTextEditor
                                value={field.value || ''}
                                onChange={field.onChange}
                                placeholder="輸入商品描述..."
                                rows={4}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="description_en"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>商品描述（英文）</FormLabel>
                            <FormControl>
                              <RichTextEditor
                                value={field.value || ''}
                                onChange={field.onChange}
                                placeholder="Enter product description in English..."
                                rows={4}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="points_required"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>所需積分 *</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  {...field} 
                                  onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="stock_quantity"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>庫存數量</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  {...field} 
                                  onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <FormField
                        control={form.control}
                        name="category"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>分類 *</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="general">一般商品</SelectItem>
                                <SelectItem value="digital">數位商品</SelectItem>
                                <SelectItem value="voucher">折價券</SelectItem>
                                <SelectItem value="gift">禮品</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="image_url"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>商品圖片網址</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="https://..." />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="is_active"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                            <div className="space-y-0.5">
                              <FormLabel>啟用狀態</FormLabel>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      
                      <div className="flex space-x-2 pt-4">
                        <Button type="submit" className="flex-1">
                          {editingReward ? '更新' : '新增'}
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setIsDialogOpen(false)}
                          className="flex-1"
                        >
                          取消
                        </Button>
                      </div>
                    </form>
                  </Form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>

        {rewards.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <div className="text-center py-8">
                <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-base font-semibold text-gray-900 mb-2">暫無獎品</h3>
                <p className="text-sm text-gray-600">點擊新增按鈕開始添加獎品</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {rewards.map((reward) => (
              <Card key={reward.id} className="p-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{reward.name}</div>
                      {reward.name_en && (
                        <div className="text-sm text-gray-500">{reward.name_en}</div>
                      )}
                      {reward.description && (
                        <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {reward.description}
                        </div>
                      )}
                    </div>
                    <Badge variant={reward.is_active ? 'default' : 'secondary'}>
                      {reward.is_active ? '啟用' : '停用'}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-xs">
                    <div>
                      <span className="text-gray-500">積分: </span>
                      <span className="font-medium">{reward.points_required}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">庫存: </span>
                      <span>{reward.stock_quantity || '無限'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">分類: </span>
                      <span>{reward.category}</span>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2 pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(reward)}
                      className="flex-1"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      編輯
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(reward.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // 桌面版表格布局
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">獎品管理</h1>
          <p className="text-gray-600 dark:text-gray-400">管理積分兌換商品</p>
        </div>
        <div className="flex space-x-2">
          <Button onClick={fetchRewards} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            重新載入
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleCreateNew}>
                <Plus className="h-4 w-4 mr-2" />
                新增獎品
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingReward ? '編輯獎品' : '新增獎品'}</DialogTitle>
              </DialogHeader>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>商品名稱（中文）*</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="name_en"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>商品名稱（英文）</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="English product name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>分類 *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="general">一般商品</SelectItem>
                            <SelectItem value="digital">數位商品</SelectItem>
                            <SelectItem value="voucher">折價券</SelectItem>
                            <SelectItem value="gift">禮品</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>商品描述（中文）</FormLabel>
                        <FormControl>
                          <RichTextEditor
                            value={field.value || ''}
                            onChange={field.onChange}
                            placeholder="輸入商品描述..."
                            rows={4}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description_en"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>商品描述（英文）</FormLabel>
                        <FormControl>
                          <RichTextEditor
                            value={field.value || ''}
                            onChange={field.onChange}
                            placeholder="Enter product description in English..."
                            rows={4}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="points_required"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>所需積分 *</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              {...field} 
                              onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="stock_quantity"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>庫存數量（0 為無限）</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              {...field} 
                              onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="image_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>商品圖片網址</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://..." />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="is_active"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">啟用狀態</FormLabel>
                          <div className="text-sm text-muted-foreground">
                            停用的商品不會在前台顯示
                          </div>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setIsDialogOpen(false)}
                    >
                      取消
                    </Button>
                    <Button type="submit">
                      {editingReward ? '更新獎品' : '新增獎品'}
                    </Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>獎品列表 ({rewards.length} 項)</CardTitle>
        </CardHeader>
        <CardContent>
          {rewards.length === 0 ? (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">暫無獎品</h3>
              <p className="text-gray-600">點擊新增獎品按鈕開始添加商品</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>商品名稱</TableHead>
                  <TableHead>分類</TableHead>
                  <TableHead>所需積分</TableHead>
                  <TableHead>庫存</TableHead>
                  <TableHead>狀態</TableHead>
                  <TableHead>建立時間</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rewards.map((reward) => (
                  <TableRow key={reward.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{reward.name}</div>
                        {reward.name_en && (
                          <div className="text-sm text-gray-500">{reward.name_en}</div>
                        )}
                        {reward.description && (
                          <div className="text-sm text-gray-500 line-clamp-1">
                            {reward.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{reward.category}</TableCell>
                    <TableCell className="font-medium">{reward.points_required}</TableCell>
                    <TableCell>{reward.stock_quantity || '無限'}</TableCell>
                    <TableCell>
                      <Badge variant={reward.is_active ? 'default' : 'secondary'}>
                        {reward.is_active ? '啟用' : '停用'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(reward.created_at).toLocaleDateString('zh-TW')}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(reward)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(reward.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RewardManagement;
